/**
 * ============================================================================
 * DREAM WEDDING GENERATOR - AI-Powered Text-to-Theme Generator
 * ============================================================================
 * 
 * PURPOSE:
 * Describe your dream wedding in words, and AI generates a complete wedding
 * theme with colors, styles, vendors, venues, and decor recommendations.
 * Like ChatGPT for wedding planning.
 * 
 * KEY FEATURES:
 * 1. Text-to-Theme Generation - Converts descriptions into full wedding themes
 * 2. Multi-Model Support - Choose Gemini, Claude, OpenAI, or compare all 3
 * 3. Parallel Compare Mode - All 3 AI models run simultaneously (3x faster)
 * 4. AI Color Magic - Explore color applications with AI suggestions
 * 5. Smart Validation - 6-layer system rejects non-wedding content
 * 6. Theme Saving - Save themes to dashboard for wedding planning
 * 
 * USER FLOW:
 * 1. Describe dream wedding in text area (e.g., "Rustic barn wedding with blush roses")
 * 2. Select AI model or compare mode
 * 3. Click "Generate My Dream Wedding"
 * 4. View generated theme with colors, styles, vendors
 * 5. Explore colors with AI Color Magic
 * 6. Save theme to dashboard
 * 
 * BACKEND INTEGRATION:
 * - POST /api/dream/generate - Single model generation
 * - POST /api/dream/compare - All 3 models in parallel (3x speedup)
 * - POST /api/dream/smart-suggestions - AI color recommendations
 * 
 * PERFORMANCE:
 * - Compare mode parallel processing: 75s → 30s (3x speedup)
 * - Real-time color name lookup with caching
 * - Debounced text input for smooth UX
 * 
 * STATE MANAGEMENT:
 * - inspirationText: User's wedding description
 * - generatedTheme: AI-generated theme data
 * - compareMode: Boolean for multi-model comparison
 * - selectedModel: Active AI model (gemini/claude/openai)
 * - colorMagicModal: AI Color Magic modal state
 * 
 * VALIDATION:
 * - 6-layer backend validation system
 * - Rejects: birthdays, graduations, non-wedding anniversaries
 * - Accepts: weddings, engagements, vow renewals
 * - Minimum text length enforcement
 * 
 * COMPARE MODE:
 * - Shows all 3 AI models side-by-side
 * - Parallel execution (3x faster than sequential)
 * - Independent error handling per model
 * - Visual comparison of themes
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';
import AIColorMagicLoader from '../../components/AIColorMagicLoader';
import AsyncColorName from '../../components/AsyncColorName';
import AIMagicButton from '../../components/AIMagicButton';
import ThemeFeedback from '../../components/ThemeFeedback';

// ============================================================================
// UTILITY IMPORTS
// ============================================================================

// Color utility functions for hex/RGB conversion, interpolation, and color analysis
import { 
  getColorName, 
  hexToRgb, 
  interpolateRgb, 
  rgbToHsl, 
  extractHexFromColor, 
  getContrastColor, 
  parseColors 
} from '../../utils/colorHelpers';

// AI-related utilities for color magic and wedding applications
import { 
  triggerAIMagic, 
  getSmartWeddingApplications, 
  getDisplayApplications 
} from '../../utils/aiHelpers';

// Notification helpers for consistent error/success messaging
import { 
  getSuccessNotification, 
  getLoginRequiredNotification, 
  getNoThemeNotification, 
  handleSaveError 
} from '../../utils/notificationHelpers';

// Style utilities for icons and categories
import { 
  styleIcons, 
  getStyleIcon, 
  categories 
} from '../../utils/styleHelpers';

// Theme rendering helpers - shared with MoodboardMatcher for consistency
import {
  renderSaveButton,
  renderElements,
  renderStyleKeywords,
  renderColorOrbs,
  renderColorCodes,
  renderColorFlow,
  renderColorPalette,
  renderAnimatedBackground,
  renderThemeHeader,
  renderRitualSection
} from '../../utils/themeRenderers';

// Modal rendering helper - shared AI Color Magic modal across all pages
import { renderAIColorMagicModal, renderRitualModal } from '../../utils/modalRenderers';

// Ritual save helpers
import { fetchSavedRitualIds, saveRitualToWeddingPlan } from '../../utils/ritualHelpers';

const DreamWeddingGenerator = () => {
  // ============================================================================
  // LOGGING UTILITY
  // ============================================================================
  const LOG_PREFIX = '[DREAM] 🎨';
  const log = {
    info: (msg, data) => console.log(`${LOG_PREFIX} ℹ️  ${msg}`, data || ''),
    success: (msg, data) => console.log(`${LOG_PREFIX} ✅ ${msg}`, data || ''),
    error: (msg, error) => console.error(`${LOG_PREFIX} ❌ ${msg}`, error || ''),
    debug: (msg, data) => console.log(`${LOG_PREFIX} 🔍 ${msg}`, data || ''),
    api: (action, data) => console.log(`${LOG_PREFIX} 🤖 API ${action}`, data || ''),
    user: (action, data) => console.log(`${LOG_PREFIX} 👤 User ${action}`, data || ''),
    quality: (msg, data) => console.log(`${LOG_PREFIX} 📊 ${msg}`, data || ''),
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // ------------------------------------------------------------------------
  // USER AUTHENTICATION STATE
  // ------------------------------------------------------------------------
  const [user, setUser] = useState(null);
  // Stores logged-in user data from AuthService
  // null = not logged in
  // object = user data (id, email, name, etc.)
  
  // ------------------------------------------------------------------------
  // FORM INPUT STATE
  // ------------------------------------------------------------------------
  const [formData, setFormData] = useState({
    text: '',           // User's wedding vision description
                        // Example: "Rustic barn wedding with blush roses and fairy lights"
                        // Min length: 10 characters (enforced by backend validation)
    
    model: 'gemini'     // Selected AI model: 'gemini', 'claude', 'openai', or 'compare'
                        // - gemini: Google Gemini 2.5 Pro (most advanced)
                        // - claude: Anthropic Claude 3.7 Sonnet (most accurate)
                        // - openai: OpenAI GPT-4 (balanced performance)
                        // - compare: Run all 3 models in parallel (3x speedup: 75s → 30s)
  });
  
  // ------------------------------------------------------------------------
  // GENERATION STATE
  // ------------------------------------------------------------------------
  const [loading, setLoading] = useState(false);
  // Loading state during API call
  // true = showing loading animation (AIColorMagicLoader)
  // false = ready for user interaction
  
  const [dreamTheme, setDreamTheme] = useState(null);
  // Generated theme(s) from backend
  // SINGLE MODE: { theme_name, colors, styles, vendors, venues, ... }
  // COMPARE MODE: { comparison: true, gemini_theme, claude_theme, openai_theme }
  // null = no theme generated yet
  
  // ------------------------------------------------------------------------
  // TIME TRACKING STATE
  // ------------------------------------------------------------------------
  // Session time tracking now handled globally in App.js via SessionTracker
  
  // ------------------------------------------------------------------------
  // AI COLOR MAGIC MODAL STATE
  // ------------------------------------------------------------------------
  const [colorModal, setColorModal] = useState({ 
    show: false,                    // Modal visibility (true = open, false = closed)
    color: '',                      // Selected color hex code (e.g., '#FFB6C1')
    position: 0,                    // Position in color flow animation (0-100%)
    colorName: '',                  // Human-readable color name (e.g., 'Blush Pink')
    applications: [],               // Wedding applications for the color:
                                    // - ['SHOW_AI_BUTTON']: Show "AI Color Magic" button
                                    // - ['LOADING_AI_MAGIC']: Show loading animation
                                    // - [...actual apps]: Show AI-generated applications
    selectedCategory: 'decor',      // Selected category for applications:
                                    // - decor, flowers, attire, stationery, cake, lighting
    sourceModel: null               // Which AI model generated this theme (for compare mode)
                                    // Used to ensure AI Color Magic uses same model
  });
  
  // ------------------------------------------------------------------------
  // COMPARE MODE STATE
  // ------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('gemini');
  const [compareFeedback, setCompareFeedback] = useState({
    gemini: { rating: 0, thumbs: null },
    claude: { rating: 0, thumbs: null },
    openai: { rating: 0, thumbs: null }
  });
  // Active tab in compare mode
  // Options: 'gemini', 'claude', 'openai'
  // Switches between different AI model results
  // Only used when formData.model === 'compare'
  
  // ------------------------------------------------------------------------
  // VENDOR BANNER STATE
  // ------------------------------------------------------------------------
  const [vendorBanners, setVendorBanners] = useState({});

  // ------------------------------------------------------------------------
  // RITUAL MODAL STATE
  // ------------------------------------------------------------------------
  const [ritualModal, setRitualModal] = useState({
    show: false,
    ritual: null
  });
  const [savedRitualIds, setSavedRitualIds] = useState(new Set());

  useEffect(() => { fetchSavedRitualIds().then(setSavedRitualIds); }, []);
  useEffect(() => { if (ritualModal.show) fetchSavedRitualIds().then(setSavedRitualIds); }, [ritualModal.show]);

  const handleSaveRitual = async (ritual) => {
    const result = await saveRitualToWeddingPlan(ritual);
    if (result.success) setSavedRitualIds(prev => new Set([...prev, ritual.id]));
    setNotification(result.notification);
  };

  /**
   * Handle tab change in compare mode
   * Switches between Gemini/Claude/OpenAI results
   */
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  // ============================================================================
  // AI COLOR MAGIC WRAPPER FUNCTIONS
  // ============================================================================
  // These wrappers pass local state to imported AI helper functions
  // Allows reusable utility functions to access component state
  // ============================================================================

  /**
   * Trigger AI Magic to get wedding applications for selected color
   * 
   * FLOW:
   * 1. Determine which AI model to use (compare mode uses gemini by default)
   * 2. Call triggerAIMagic utility function
   * 3. Show loading animation
   * 4. Fetch AI-generated color applications from backend
   * 5. Update modal with applications
   */
  const handleTriggerAIMagic = async () => {
    // In compare mode, use gemini as default (or use sourceModel if available)
    const selectedModel = formData.model === 'compare' ? 'gemini' : formData.model;
    await triggerAIMagic(colorModal, setColorModal, selectedModel, apiService);
  };

  /**
   * Get display applications for selected color in modal
   * Formats applications for UI display
   * Handles loading state and error state
   */
  const handleGetDisplayApplications = () => {
    return getDisplayApplications(colorModal);
  };

  // Function to get color name from hex using api.color.pizza
  // const getSmartWeddingApplications = async (hex, colorName, selectedModel) => {
  //   try {
  //     const requestData = {
  //       color: hex,
  //       colorName: colorName,
  //       model: selectedModel,
  //       timestamp: Date.now()
  //     };
  //     
  //     console.log('🚀 Making POST request to smart suggestions API:', requestData);
  //     
  //     // Use apiService.dream pattern like other calls
  //     const response = await apiService.dream.generateSmartSuggestions(requestData);
  //
  //     console.log('📡 Response status:', response.status);
  //     console.log('📡 Response data:', response.data);
  //
  //     if (response.status === 200 && response.data) {
  //       // Try to parse JSON response for categorized suggestions
  //       try {
  //         let suggestions = response.data.suggestions;
  //         if (typeof suggestions === 'string') {
  //           // Try to parse JSON from string
  //           const jsonMatch = suggestions.match(/\{[\s\S]*\}/);
  //           if (jsonMatch) {
  //             suggestions = JSON.parse(jsonMatch[0]);
  //           }
  //         }
  //         
  //         if (suggestions && typeof suggestions === 'object' && suggestions.decor) {
  //           return suggestions; // Return categorized object
  //         }
  //       } catch (parseError) {
  //         console.log('JSON parse failed, using fallback');
  //       }
  //       
  //       // Return error indicator instead of hardcoded suggestions
  //       return ['API_ERROR'];
  //     } else {
  //       return ['API_ERROR'];
  //     }
  //   } catch (error) {
  //     console.error('Error getting smart wedding applications:', error);
  //     return ['API_ERROR'];
  //   }
  // };


  const getWeddingApplications = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return ['Accent decorations & centerpieces', 'Bridesmaid coordination', 'Floral highlights', 'Lighting effects'];
    
    const { r, g, b } = rgb;
    const hsl = rgbToHsl(r, g, b);
    
    // Dynamic suggestions based on color properties
    if (hsl.l > 80) { // Light colors
      return ['Delicate table linens & napkins', 'Soft ceremony backdrop', 'Bridal party accessories', 'Subtle lighting accents'];
    } else if (hsl.l < 30) { // Dark colors
      return ['Elegant evening lighting', 'Formal suit accents', 'Dramatic backdrop elements', 'Sophisticated table settings'];
    } else if (hsl.s > 70) { // Vibrant colors
      return ['Bold floral arrangements', 'Statement ceremony arch', 'Vibrant bridesmaid dresses', 'Eye-catching centerpieces'];
    } else { // Muted colors
      return ['Rustic table decorations', 'Natural ceremony elements', 'Vintage-style accessories', 'Soft ambient lighting'];
    }
  };
  const [error, setError] = useState(null);
  const [serverStatus, setServerStatus] = useState('checking');
  const [apiStatus, setApiStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ 
    show: false, 
    message: '', 
    type: '', 
    title: '' 
  });


  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Fetch user profile
      apiService.users.getProfile()
        .then(response => {
          if (response.data.success) {
            setUser(response.data.user);
          }
        })
        .catch(error => {
          console.error('Error fetching user profile:', error);
        });
    }
  }, []);

  // Check server connectivity on component mount
  useEffect(() => {
    checkServerConnection();
    setIsVisible(true);
  }, []);

  const checkServerConnection = async () => {
    try {
      console.log('Checking server connection...');
      const response = await apiService.health();
      console.log('Server health check response:', response.data);
      setServerStatus('connected');
      
      // Also check API status
      checkApiStatus();
    } catch (error) {
      console.error('Server connection failed:', error);
      setServerStatus('disconnected');
      setError('Cannot connect to server. Please check your internet connection and try again.');
    }
  };

  const checkApiStatus = async () => {
    try {
      const response = await apiService.dream.test();
      setApiStatus(response.data.results);
      console.log('API status:', response.data.results);
    } catch (error) {
      console.error('API status check failed:', error);
    }
  };

  // Handle text input changes
  const handleTextChange = (e) => {
    setFormData({
      ...formData,
      text: e.target.value
    });
  };

  // Handle AI model selection changes
  const handleModelChange = (e) => {
    setFormData({
      ...formData,
      model: e.target.value
    });
  };

  // ============================================================================
  // THEME GENERATION FUNCTION
  // ============================================================================
  // Main function: Submits user's wedding vision to backend for AI processing
  // 
  // FLOW:
  // 1. Validate server connection and text input
  // 2. Route to appropriate API endpoint (single vs compare mode)
  // 3. Backend processes with AI (6-layer validation + theme generation)
  // 4. Receive and display generated theme(s)
  // 5. Show success animation (confetti)
  // 
  // BACKEND PROCESSING (Compare Mode):
  // - Sequential (old): 75s (30s + 25s + 20s)
  // - Parallel (new): 30s (max of all 3) - 3x faster!
  // ============================================================================

  const handleSubmit = async (e) => {
    e.preventDefault();  // Prevent form submission page reload
    const startTime = Date.now();
    
    log.info('=== FORM SUBMISSION ===');
    log.user('Submit clicked', { 
      textLength: formData.text?.length || 0, 
      model: formData.model 
    });
    
    // ========================================================================
    // VALIDATION: CHECK SERVER CONNECTION
    // ========================================================================
    if (serverStatus !== 'connected') {
      log.error('Server not connected');
      setError('Server is not connected. Please check your connection and try again.');
      return;
    }
    
    // ========================================================================
    // VALIDATION: CHECK TEXT INPUT
    // ========================================================================
    if (!formData.text.trim()) {
      log.error('Empty text input');
      setError('Please provide some inspiration text');
      return;
    }

    log.info('Validation passed');

    // ========================================================================
    // INITIALIZE LOADING STATE
    // ========================================================================
    setLoading(true);                    // Show loading animation
    setError(null);                      // Clear previous errors
    setDreamTheme(null);                 // Clear previous theme immediately
    setVendorBanners({});                 // Hide all vendor banners when generating new theme
    setCompareFeedback({ gemini: { rating: 0, thumbs: null }, claude: { rating: 0, thumbs: null }, openai: { rating: 0, thumbs: null } });

    try {
      log.api('Request starting', { 
        model: formData.model, 
        textLength: formData.text.length 
      });
      
      // ======================================================================
      // STEP 1: ROUTE TO APPROPRIATE API ENDPOINT
      // ======================================================================
      let response;
      if (formData.model === 'compare') {
        // ----------------------------------------------------------------------
        // COMPARE MODE: All 3 AI models run in parallel
        // ----------------------------------------------------------------------
        log.info('Compare mode - calling all 3 models');
        response = await apiService.dream.compareModels(formData);
      } else {
        // ----------------------------------------------------------------------
        // SINGLE MODE: One AI model (gemini/claude/openai)
        // ----------------------------------------------------------------------
        log.info('Single mode', { model: formData.model });
        response = await apiService.dream.generateTheme(formData);
      }
      
      const duration = Date.now() - startTime;
      log.success('Response received', { duration: `${duration}ms` });
      log.debug('Response data', response.data);
      
      // ======================================================================
      // STEP 2: PROCESS RESPONSE AND UPDATE STATE
      // ======================================================================
      // Handle different response structures from backend
      if (response.data && response.data.theme) {
        // Single mode: { theme: {...} }
        log.info('Single theme received', { 
          themeName: response.data.theme.theme_name,
          model: response.data.theme.generated_by,
          qualityScore: response.data.theme.quality_score
        });
        setDreamTheme(response.data.theme);
        trackGeneratedEvent(response.data.theme);
      } else {
        // Compare mode: { comparison: true, gemini_theme, claude_theme, openai_theme }
        log.info('Compare mode themes received', {
          hasGemini: !!response.data.gemini_theme,
          hasClaude: !!response.data.claude_theme,
          hasOpenAI: !!response.data.openai_theme
        });
        setDreamTheme(response.data);
        // Track generated event for each model's theme in compare mode
        ['gemini_theme', 'claude_theme', 'openai_theme'].forEach(key => {
          if (response.data[key]) trackGeneratedEvent(response.data[key]);
        });
      }

      // ======================================================================
      // STEP 3: SHOW SUCCESS ANIMATION
      // ======================================================================
      log.success('Theme generation complete', { totalDuration: `${Date.now() - startTime}ms` });
      // Trigger confetti animation for 3 seconds
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      
    } catch (error) {
      // ======================================================================
      // ERROR HANDLING
      // ======================================================================
      const duration = Date.now() - startTime;
      log.error('Theme generation failed', error);
      log.debug('Error details', { 
        duration: `${duration}ms`,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Default error message
      let errorMessage = 'Unable to generate your dream theme right now. Please try again in a moment.';
      
      // Extract specific error message from response
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
        log.error('API error response', { 
          status: error.response.status, 
          message: errorMessage 
        });
      } else if (error.request) {
        errorMessage = 'Cannot reach the server. Please check your connection.';
        log.error('Network error - no response received');
      }
      
      setError(errorMessage);
      setDreamTheme(null); // Clear any previous theme when error occurs
    } finally {
      setLoading(false);
      log.info('Form submission complete', { duration: `${Date.now() - startTime}ms` });
    }
  };

  const clearForm = () => {
    setFormData({ text: '', model: 'gemini' });
    setDreamTheme(null);
    setError(null);
    setShowConfetti(false);
  };

  const extractNameFromColor = async (colorString) => {
    if (!colorString) return 'Unknown Color';
    
    // Extract name from "Name (#HEX)" format first
    const nameMatch = colorString.match(/^(.+?)\s*\(#[0-9A-Fa-f]{6}\)$/);
    if (nameMatch) {
      return nameMatch[1].trim();
    }
    
    // If it's just a hex code or no name provided, get name from API
    let hexCode = colorString;
    if (colorString.startsWith('#')) {
      hexCode = colorString;
    } else {
      // Try to extract hex from any format
      const hexMatch = colorString.match(/#[0-9A-Fa-f]{6}/);
      if (hexMatch) {
        hexCode = hexMatch[0];
      } else {
        return colorString; // Return original if no hex found
      }
    }
    
    // Use the same API system as AI Color Magic
    return await getColorName(hexCode);
  };

  // ============================================================================
  // FEEDBACK HANDLER
  // ============================================================================
  // Handles feedback submission success/error notifications
  // ============================================================================

  const handleFeedbackSuccess = (message, type = 'success') => {
    setNotification({
      show: true,
      message: message,
      type: type,
      title: type === 'success' ? 'Thank you!' : 'Error'
    });
  };

  // ============================================================================
  // SAVE RATE TRACKING HELPER
  // ============================================================================
  // Tracks 'generated' event for save rate calculation
  // ============================================================================

  const trackGeneratedEvent = async (theme) => {
    try {
      await apiService.feedback.trackGenerated({
        feature_type: 'dream_generator',
        ai_model: theme.generated_by,
        quality_score: theme.quality_score,
        theme_name: theme.theme_name
      });
    } catch (error) {
      console.error('Error tracking generated event:', error);
    }
  };

  // ============================================================================
  // SAVE THEME FUNCTION
  // ============================================================================
  // Saves generated theme to user's dashboard
  // Handles authentication, validation, and error states
  // Shows vendor banner after successful save
  // ============================================================================

  const saveTheme = async () => {
    console.log('User:', user);
    console.log('Dream theme:', dreamTheme);
    
    // Check token directly instead of relying on user state
    const token = localStorage.getItem('token');
    console.log('Token exists:', !!token);
    
    if (!token) {
      console.log('No token found, showing login message');
      setNotification(getLoginRequiredNotification());
      return;
    }

    // Validate theme data exists
    if (!dreamTheme || !dreamTheme.theme_name) {
      setNotification(getNoThemeNotification());
      return;
    }

    try {
      console.log('Starting save process...');
      setSaving(true);
      
      // Prepare theme data for saving
      const themeData = {
        theme_name: dreamTheme.theme_name,
        description: dreamTheme.description,
        colors: dreamTheme.colors || [],
        style_keywords: dreamTheme.style_keywords || [],
        elements: dreamTheme.elements || [],
        ai_model: dreamTheme.generated_by || formData.model || 'gemini',
        inspiration_text: formData.text
      };

      console.log('Theme data prepared:', themeData);

      console.log('Making API call to save theme...');
      const response = await apiService.savedThemes.save(themeData);
      console.log('API response:', response);
      
      if (response.data.success) {
        console.log('Theme saved successfully!');
        
        // Update dreamTheme with saved theme ID
        setDreamTheme(prev => ({
          ...prev,
          id: response.data.theme.id
        }));
        
        // Track 'saved' event for save rate calculation
        try {
          await apiService.feedback.trackSaved({
            feature_type: 'dream_generator',
            content_id: response.data.theme.id,
            ai_model: dreamTheme.generated_by,
            theme_name: dreamTheme.theme_name,
            quality_score: dreamTheme.quality_score
          });
        } catch (error) {
          console.error('Error tracking saved event:', error);
        }
        
        setNotification(getSuccessNotification('', 'dream'));
        
        // Show vendor matching banner after successful save
        setTimeout(() => {
          const bannerKey = (dreamTheme.generated_by || formData.model || 'gemini').toLowerCase();
          setVendorBanners(prev => ({ ...prev, [bannerKey]: true }));
        }, 2000); // Show banner 2 seconds after success notification
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error code:', error.response?.data?.code);
      
      const { notification, showBanner } = handleSaveError(error);
      setNotification(notification);
      
      // Show vendor banner for duplicate themes (theme already exists)
      if (showBanner) {
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [dreamTheme.generated_by || 'single']: true }));
        }, 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveIndividualTheme = async (themeToSave, modelName) => {
    console.log('Save individual theme button clicked!');
    console.log('Theme to save:', themeToSave);
    console.log('Model name:', modelName);
    
    const token = localStorage.getItem('token');
    console.log('Token exists:', !!token);
    
    if (!token) {
      console.log('No token found, showing login message');
      setNotification(getLoginRequiredNotification());
      return;
    }

    if (!themeToSave || !themeToSave.theme_name) {
      setNotification(getNoThemeNotification());
      return;
    }

    setSaving(true);

    try {
      const themeData = {
        theme_name: themeToSave.theme_name,
        description: themeToSave.description,
        colors: themeToSave.colors || [],
        style_keywords: themeToSave.style_keywords || [],
        elements: themeToSave.elements || [],
        ai_model: modelName.toLowerCase(),
        inspiration_text: formData.text,
        inspiration_source: 'dream_wedding_generator'
      };

      console.log('Saving individual theme data:', themeData);

      const response = await apiService.savedThemes.save(themeData);
      console.log('Save response:', response.data);

      if (response.data.success) {
        // Update the specific model's theme with saved ID
        const modelKey = modelName.toLowerCase() + '_theme';
        setDreamTheme(prev => ({
          ...prev,
          [modelKey]: { ...prev[modelKey], id: response.data.theme.id }
        }));

        // Track 'saved' event for save rate calculation
        try {
          await apiService.feedback.trackSaved({
            feature_type: 'dream_generator',
            content_id: response.data.theme.id,
            ai_model: modelName.toLowerCase(),
            theme_name: themeToSave.theme_name,
            quality_score: themeToSave.quality_score
          });
        } catch (error) {
          console.error('Error tracking saved event:', error);
        }

        setNotification(getSuccessNotification(modelName, 'dream'));
        
        // Show vendor matching banner after successful save
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [modelName.toLowerCase()]: true }));
        }, 2000); // Show banner 2 seconds after success notification
      }
    } catch (error) {
      console.error('Error saving individual theme:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error code:', error.response?.data?.code);
      
      const { notification, showBanner } = handleSaveError(error);
      setNotification(notification);
      
      // Show vendor banner for duplicate themes (theme already exists)
      if (showBanner) {
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [modelName.toLowerCase()]: true }));
        }, 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  const dreamGeneratorIcons = [
    { emoji: '💍', size: '4xl', opacity: '10' },
    { emoji: '💖', size: '4xl', opacity: '10' },
    { emoji: '🥂', size: '4xl', opacity: '10' },
    { emoji: '✨', size: '4xl', opacity: '10' },
    { emoji: '🦋', size: '4xl', opacity: '10' }
  ];


  // ============================================================================
  // THEME RENDERING WRAPPER FUNCTIONS
  // ============================================================================
  // These wrappers pass local state and callbacks to imported theme renderers
  // Ensures consistent rendering across individual and compare modes
  // Matches MoodboardMatcher pattern for consistency
  // ============================================================================

  // Wrapper for save button - passes all required state and callbacks to helper
  // Supports both main theme save (saveTheme) and individual model save (saveIndividualTheme)
  const wrappedRenderSaveButton = (themeData, modelName, customSaveFunction = null) => {
    const saveFn = customSaveFunction || saveIndividualTheme;
    const modelKey = (themeData.generated_by || modelName).toLowerCase();
    const showBanner = vendorBanners[modelKey] || false;
    const setBanner = (val) => setVendorBanners(prev => ({ ...prev, [modelKey]: val }));
    return renderSaveButton(themeData, modelName, saveFn, saving, showBanner, setBanner, null, setNotification);
  };

  // Wrapper for color orbs - passes modal state and color name fetcher
  const wrappedRenderColorOrbs = (colors, sourceModel, style) => {
    return renderColorOrbs(colors, sourceModel, style, setColorModal, AsyncColorName);
  };

  // Wrapper for color flow visualization - passes modal state setter
  const wrappedRenderColorFlow = (colors, sourceModel) => {
    return renderColorFlow(colors, sourceModel, setColorModal);
  };

  const wrappedRenderColorPalette = (colors, sourceModel, style) => {
    return renderColorPalette(colors, sourceModel, style, wrappedRenderColorOrbs, renderColorCodes, wrappedRenderColorFlow);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={dreamGeneratorIcons} />
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating sparkles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10 hover:opacity-30 transition-opacity duration-300"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg animate-bounce" style={{ animationDelay: `${Math.random() * 2}s` }}>✨</span>
          </div>
        ))}

        {/* Confetti Animation */}
        {showConfetti && (
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className={`absolute w-2 h-2 bg-gradient-to-r ${i % 3 === 0 ? 'from-rose-400 to-pink-500' : i % 3 === 1 ? 'from-gold-400 to-yellow-500' : 'from-purple-400 to-pink-500'} rounded-full animate-ping`}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              ></div>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-3xl animate-pulse">🪄</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Dream Wedding Generator
          </h1>
          
          {/* Gold accent underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400 animate-bounce">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Describe your perfect day and let AI bring it to life with personalized themes, colors, and inspiration
          </p>
        </div>

        {/* Input Section */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                    <span className="text-2xl animate-bounce">💭</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Describe Your Dream Wedding
                  </h3>
                </div>
                
                <textarea
                  value={formData.text}
                  onChange={handleTextChange}
                  placeholder="Tell us your vision… from colors to vibe, location, and style. Be as detailed as you'd like! (e.g., 'A romantic garden ceremony at sunset with fairy lights, vintage decorations, soft pink and white flowers, and a rustic wooden arch')"
                  className="w-full px-6 py-4 text-lg border-2 border-gold-200 rounded-2xl focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-gradient-to-r from-white to-rose-25 resize-none"
                  rows="6"
                  required
                />
                <p className="text-sm text-gray-500 mt-3 flex items-center">
                  <span className="mr-2">💡</span>
                  Be specific! Mention style, colors, venue, decorations, flowers, or any special elements you envision.
                </p>
              </div>

              {/* AI Model Selection Section */}
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-blue-400 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                    <span className="text-xl animate-bounce">🤖</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800">Choose AI Model</h4>
                </div>
                
                {/* Model Selection Grid - 4 options: Gemini, Claude, OpenAI, Compare */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Gemini Selection - Google's latest AI model */}
                  <label className="relative cursor-pointer group">
                    <input
                      type="radio"
                      name="model"
                      value="gemini"
                      checked={formData.model === 'gemini'}
                      onChange={handleModelChange}
                      className="sr-only"
                    />
                    <div className={`relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-25 to-purple-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                      formData.model === 'gemini' 
                        ? 'border-blue-400 shadow-2xl ring-4 ring-blue-200/50 scale-105' 
                        : 'border-blue-200/30 shadow-lg hover:border-blue-300'
                    }`}>
                      {/* Magical sparkle background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-purple-100/20"></div>
                      <div className="absolute top-2 right-2 text-blue-300 animate-pulse">✨</div>
                      
                      <div className="relative p-4 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-lg ring-2 ring-blue-200">
                          <img src="/gemini.png" alt="Gemini" className="w-8 h-8 object-contain" />
                        </div>
                        <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">Gemini</h5>
                        <p className="text-xs text-gray-600 leading-relaxed">Google's Latest AI</p>
                      </div>
                    </div>
                  </label>
                  
                  {/* Claude AI Selection - Anthropic's Claude model */}
                  <label className="relative cursor-pointer group">
                    <input
                      type="radio"
                      name="model"
                      value="claude"
                      checked={formData.model === 'claude'}
                      onChange={handleModelChange}
                      className="sr-only"
                    />
                    <div className={`relative overflow-hidden bg-gradient-to-br from-orange-50 via-amber-25 to-yellow-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                      formData.model === 'claude' 
                        ? 'border-orange-400 shadow-2xl ring-4 ring-orange-200/50 scale-105' 
                        : 'border-orange-200/30 shadow-lg hover:border-orange-300'
                    }`}>
                      {/* Warm glow background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-100/20 to-amber-100/20"></div>
                      <div className="absolute top-2 right-2 text-orange-300 animate-pulse">🌟</div>
                      
                      <div className="relative p-4 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-lg ring-2 ring-orange-200">
                          <img src="/claude.png" alt="Claude" className="w-8 h-8 object-contain" />
                        </div>
                        <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-1">Claude</h5>
                        <p className="text-xs text-gray-600 leading-relaxed">by Anthropic</p>
                      </div>
                    </div>
                  </label>
                  
                  {/* OpenAI Selection - OpenAI's GPT model */}
                  <label className="relative cursor-pointer group">
                    <input
                      type="radio"
                      name="model"
                      value="openai"
                      checked={formData.model === 'openai'}
                      onChange={handleModelChange}
                      className="sr-only"
                    />
                    <div className={`relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-25 to-teal-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                      formData.model === 'openai' 
                        ? 'border-emerald-400 shadow-2xl ring-4 ring-emerald-200/50 scale-105' 
                        : 'border-emerald-200/30 shadow-lg hover:border-emerald-300'
                    }`}>
                      {/* Fresh glow background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 to-green-100/20"></div>
                      <div className="absolute top-2 right-2 text-emerald-300 animate-pulse">💫</div>
                      
                      <div className="relative p-4 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-lg ring-2 ring-emerald-200">
                          <img src="/openai.png" alt="OpenAI" className="w-8 h-8 object-contain" />
                        </div>
                        <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-1">OpenAI</h5>
                        <p className="text-xs text-gray-600 leading-relaxed">GPT Model</p>
                      </div>
                    </div>
                  </label>
                  
                  {/* Compare Mode - Generate with both models for side-by-side comparison */}
                  <label className="relative cursor-pointer group">
                    <input
                      type="radio"
                      name="model"
                      value="compare"
                      checked={formData.model === 'compare'}
                      onChange={handleModelChange}
                      className="sr-only"
                    />
                    <div className={`relative overflow-hidden bg-gradient-to-br from-rose-50 via-pink-25 to-gold-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                      formData.model === 'compare' 
                        ? 'border-gold-400 shadow-2xl ring-4 ring-gold-200/50 scale-105' 
                        : 'border-gold-200/30 shadow-lg hover:border-gold-300'
                    }`}>
                      {/* Luxurious glow background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-rose-100/20 to-gold-100/20"></div>
                      <div className="absolute top-2 right-2 text-gold-400 animate-pulse">💎</div>
                      
                      <div className="relative p-4 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-rose-400 to-gold-400 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-2xl animate-bounce">⚖️</span>
                        </div>
                        <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-rose-600 to-gold-600 bg-clip-text text-transparent mb-1">Compare</h5>
                        <p className="text-xs text-gray-600 leading-relaxed">Tab View</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg animate-shake">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">⚠️</span>
                    <span className="font-semibold">{error}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading || serverStatus !== 'connected'}
                  className="flex-1 relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-pulse"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      ✨ Generating Magic...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2 text-xl animate-bounce">🎯</span>
                      Generate Dream Theme
                    </div>
                  )}
                  
                  {/* Gentle pulse glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
                </button>
                
                <button
                  type="button"
                  onClick={clearForm}
                  className="group relative overflow-hidden bg-white/80 backdrop-blur-sm text-rose-600 px-8 py-4 rounded-2xl font-bold text-lg border-2 border-rose-200 hover:bg-rose-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <span className="mr-3 text-xl animate-bounce">🔄</span>
                  Clear All
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Single Model Results Display - Shows when one AI model generates a theme */}
        {dreamTheme && dreamTheme.generated_by && (
          <div className={`transition-all duration-700 delay-500 ${dreamTheme ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative bg-white/60 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
              
              {renderThemeHeader(dreamTheme.generated_by || 'ai', 
                dreamTheme.generated_by === 'gemini' ? '/gemini.png' : 
                dreamTheme.generated_by === 'claude' ? '/claude.png' : 
                dreamTheme.generated_by === 'openai' ? '/openai.png' : null, 
                'individual')}
              
              {(dreamTheme.generated_by === 'claude' || dreamTheme.generated_by === 'openai' || dreamTheme.generated_by === 'gemini') && (
                <div className="flex items-center justify-center mb-6">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold border shadow-lg ${
                    dreamTheme.generated_by === 'claude' 
                      ? 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-700 border-orange-200'
                      : dreamTheme.generated_by === 'openai'
                      ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-700 border-green-200'
                      : 'bg-gradient-to-r from-blue-100 to-purple-50 text-blue-700 border-blue-200'
                  }`}>
                    <span className="animate-bounce">🤖</span> Generated by {
                      dreamTheme.generated_by === 'claude' ? 'Claude (Anthropic)' : 
                      dreamTheme.generated_by === 'openai' ? 'OpenAI GPT' : 
                      'Gemini (Google)'
                    }
                  </span>
                </div>
              )}
              
              {/* Spectacular Theme Display */}
              <div className="bg-white p-8 rounded-3xl shadow-2xl border-2 border-gray-100 relative overflow-hidden mb-8">
                {/* Animated gradient background */}
                <div className="absolute inset-0 opacity-10">
                  <div 
                    className="absolute inset-0 animate-pulse"
                    style={{
                      background: `linear-gradient(45deg, ${dreamTheme.colors ? parseColors(dreamTheme.colors).map(color => extractHexFromColor(color) + '20').join(', ') : '#E5E7EB20'})`
                    }}
                  ></div>
                </div>
                
                <div className="relative z-10 space-y-10">
                  
                  {/* Theme Description */}
                  <div className="group">
                    <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">
                      <span className="mr-3 text-3xl animate-bounce">📝</span> {dreamTheme.theme_name}
                    </h3>
                    <div className="bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 p-6 rounded-3xl border-2 border-rose-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                      <p className="text-gray-700 leading-relaxed text-lg font-light">
                        {dreamTheme.description}
                      </p>
                    </div>
                  </div>

                  {/* Inspiration Text */}
                  {dreamTheme.inspiration_text && (
                    <div className="group">
                      <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gold-600 group-hover:to-yellow-600 group-hover:bg-clip-text transition-all duration-300">
                        <span className="mr-3 text-3xl animate-bounce">💡</span> Original Inspiration
                      </h3>
                      <div className="bg-gradient-to-br from-gold-50 via-yellow-25 to-champagne-50 p-6 rounded-3xl border-2 border-gold-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                        <p className="text-gray-700 leading-relaxed text-lg font-light italic">
                          "{dreamTheme.inspiration_text}"
                        </p>
                      </div>
                    </div>
                  )}

                  {wrappedRenderColorPalette(dreamTheme.colors, dreamTheme.generated_by, 'individual')}
                  {renderStyleKeywords(dreamTheme.style_keywords, 'individual')}
                  {renderElements(dreamTheme.elements, 'individual')}
                </div>
              </div>

              {/* Save to Dashboard Button - Uses wrapper for consistency */}
              {wrappedRenderSaveButton(dreamTheme, 'Dream Wedding', saveTheme)}

              {/* Ritual Suggestions Section */}
              {renderRitualSection(dreamTheme.suggested_rituals, dreamTheme.theme_name, setRitualModal, null, dreamTheme.id)}

              {/* Theme Feedback Section */}
              <ThemeFeedback
                featureType="dream_generator"
                contentId={null}
                themeName={dreamTheme.theme_name}
                aiModel={dreamTheme.generated_by}
                qualityScore={dreamTheme.quality_score}
                onSuccess={handleFeedbackSuccess}
              />
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* COMPARE MODE - AI MODEL COMPARISON RESULTS                       */}
        {/* ================================================================ */}
        {/* Shows side-by-side comparison of Gemini, Claude, and OpenAI     */}
        {/* Users can switch between tabs to view each model's results      */}
        {/* All tabs use consistent helper functions for rendering          */}
        {/* Includes magical tab buttons with sparkles and animations       */}
        {/* ================================================================ */}
        
        {dreamTheme && (dreamTheme.gemini_theme || dreamTheme.claude_theme || dreamTheme.openai_theme) && (
          <div className={`transition-all duration-700 delay-500 ${dreamTheme ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            
            {/* Enhanced Comparison Header with VowAI Styling */}
            <div className="relative bg-white/60 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 mb-8 hover:shadow-3xl transition-all duration-500">
              {/* Animated background gradient */}
              <div className="absolute inset-0 opacity-10">
                <div 
                  className="absolute inset-0 animate-pulse"
                  style={{
                    background: `linear-gradient(45deg, ${dreamTheme.claude_theme?.colors ? parseColors(dreamTheme.claude_theme.colors).map(color => extractHexFromColor(color) + '20').join(', ') : '#E5E7EB20'}, ${dreamTheme.openai_theme?.colors ? parseColors(dreamTheme.openai_theme.colors).map(color => extractHexFromColor(color) + '20').join(', ') : '#E5E7EB20'})`
                  }}
                ></div>
              </div>

              <div className="relative z-10 text-center">
                <div className="flex items-center justify-center gap-6 mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-400 via-rose-400 to-green-400 rounded-full shadow-2xl animate-bounce">
                    <span className="text-3xl animate-pulse">⚖️</span>
                  </div>
                  <h3 className="text-5xl font-serif font-bold bg-gradient-to-r from-orange-600 via-rose-600 to-green-600 bg-clip-text text-transparent">
                    AI Model Comparison
                  </h3>
                </div>
                
                <div className="w-32 h-1 bg-gradient-to-r from-orange-400 via-rose-400 to-green-500 mx-auto rounded-full mb-8"></div>
                
                {/* Tab Navigation */}
                <div className="flex justify-center gap-6 mb-8">
                  {[
                    { key: 'gemini', name: 'Gemini', available: dreamTheme.gemini_theme, color: 'blue', icon: '🔮', gradient: 'from-blue-500 via-indigo-500 to-purple-500' },
                    { key: 'claude', name: 'Claude', available: dreamTheme.claude_theme, color: 'orange', icon: '🎨', gradient: 'from-orange-500 via-rose-500 to-pink-500' },
                    { key: 'openai', name: 'OpenAI', available: dreamTheme.openai_theme, color: 'green', icon: '✨', gradient: 'from-green-500 via-emerald-500 to-teal-500' }
                  ].filter(tab => tab.available).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => handleTabChange(tab.key)}
                      className={`group relative overflow-hidden px-8 py-4 rounded-3xl font-serif font-bold text-lg transition-all duration-500 transform hover:scale-110 hover:-rotate-1 ${
                        activeTab === tab.key
                          ? `bg-gradient-to-r ${tab.gradient} text-white shadow-2xl scale-110 ring-4 ring-white/30`
                          : 'bg-white/80 backdrop-blur-lg text-gray-700 hover:bg-white/95 shadow-xl hover:shadow-2xl border-2 border-white/50'
                      }`}
                    >
                      {/* Magical background shimmer */}
                      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                        activeTab === tab.key ? 'opacity-30' : ''
                      }`}>
                        <div className={`absolute inset-0 bg-gradient-to-r ${tab.gradient} animate-pulse`}></div>
                      </div>
                      
                      {/* Floating sparkles */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute top-1 left-2 text-xs animate-bounce delay-100">✨</div>
                        <div className="absolute top-2 right-3 text-xs animate-bounce delay-300">💫</div>
                        <div className="absolute bottom-1 left-1/2 text-xs animate-bounce delay-500">⭐</div>
                      </div>
                      
                      <div className="relative z-10 flex items-center justify-center gap-3">
                        <span className={`text-2xl transition-transform duration-300 ${
                          activeTab === tab.key ? 'animate-pulse scale-125' : 'group-hover:animate-bounce'
                        }`}>
                          {tab.icon}
                        </span>
                        <span className="bg-gradient-to-r from-current to-current bg-clip-text">
                          {tab.name}
                        </span>
                        {activeTab === tab.key && (
                          <span className="text-sm opacity-90 animate-pulse">Results</span>
                        )}
                      </div>
                      
                      {/* Active indicator */}
                      {activeTab === tab.key && (
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-white rounded-full animate-pulse"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ============================================================ */}
            {/* TABBED MODEL RESULTS DISPLAY                                 */}
            {/* ============================================================ */}
            {/* Renders the selected AI model's theme results               */}
            {/* Each tab (Gemini/Claude/OpenAI) uses the same structure:    */}
            {/* 1. renderAnimatedBackground - color-based background        */}
            {/* 2. renderThemeHeader - model logo and name                  */}
            {/* 3. Theme & Style section - name and description             */}
            {/* 4. wrappedRenderColorPalette - colors, codes, flow          */}
            {/* 5. renderStyleKeywords - style tags                         */}
            {/* 6. renderElements - wedding elements                        */}
            {/* 7. wrappedRenderSaveButton - save with vendor banner        */}
            {/* ============================================================ */}
            
            {(() => {
              // GEMINI TAB - Google's Gemini AI results
              if (activeTab === 'gemini' && dreamTheme.gemini_theme) {
                return (
                  <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-blue-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                    
                    {renderAnimatedBackground(dreamTheme.gemini_theme?.colors, '#4F46E510')}
                    
                    {renderThemeHeader('gemini', '/gemini.png', 'compare')}
                    
                    {/* Gemini Theme Content - Using Helper Functions */}
                    <div className="space-y-6 relative z-10">
                      {/* Theme & Style Section */}
                      <div className="bg-gradient-to-br from-blue-50/90 via-indigo-50/90 to-purple-50/90 rounded-2xl p-6 border-2 border-blue-200/40 shadow-lg hover:shadow-xl transition-all duration-300 group">
                        <div className="flex items-center mb-4">
                          <span className="text-2xl mr-3 animate-bounce">🎨</span>
                          <h5 className="text-xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text transition-all duration-300">Theme & Style</h5>
                        </div>
                        <h6 className="text-2xl font-bold text-gray-800 mb-3">{dreamTheme.gemini_theme?.theme_name || 'Theme Name'}</h6>
                        <p className="text-gray-700 leading-relaxed font-light">{dreamTheme.gemini_theme?.description || 'Theme description'}</p>
                      </div>

                      {/* Inspiration Text */}
                      {formData.text && (
                        <div className="group">
                          <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gold-600 group-hover:to-yellow-600 group-hover:bg-clip-text transition-all duration-300">
                            <span className="mr-3 text-3xl animate-bounce">💡</span> Original Inspiration
                          </h3>
                          <div className="bg-gradient-to-br from-gold-50 via-yellow-25 to-champagne-50 p-6 rounded-3xl border-2 border-gold-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                            <p className="text-gray-700 leading-relaxed text-lg font-light italic">
                              "{formData.text}"
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Color Palette */}
                      {wrappedRenderColorPalette(dreamTheme.gemini_theme?.colors, 'gemini', 'individual')}

                      {/* Style Keywords */}
                      {dreamTheme.gemini_theme?.style_keywords && renderStyleKeywords(dreamTheme.gemini_theme.style_keywords, 'individual')}

                      {/* Key Elements */}
                      {dreamTheme.gemini_theme?.elements && renderElements(dreamTheme.gemini_theme.elements, 'individual')}
                    </div>

                    {/* Save Gemini Theme Button */}
                    {wrappedRenderSaveButton(dreamTheme.gemini_theme, 'Gemini')}

                    {/* Ritual Suggestions */}
                    {renderRitualSection(dreamTheme.gemini_theme?.suggested_rituals, dreamTheme.gemini_theme?.theme_name, setRitualModal, null, dreamTheme.gemini_theme?.id)}

                    {/* Theme Feedback Section */}
                    <ThemeFeedback
                      key="gemini"
                      featureType="dream_generator"
                      contentId={null}
                      themeName={dreamTheme.gemini_theme?.theme_name}
                      aiModel={dreamTheme.gemini_theme?.generated_by}
                      qualityScore={dreamTheme.gemini_theme?.quality_score}
                      initialRating={compareFeedback.gemini.rating}
                      initialThumbs={compareFeedback.gemini.thumbs}
                      onFeedbackChange={(r, t) => setCompareFeedback(prev => ({ ...prev, gemini: { rating: r, thumbs: t } }))}
                      onSuccess={handleFeedbackSuccess}
                    />
                  </div>
                );
              
              // CLAUDE TAB - Anthropic's Claude AI results
              } else if (activeTab === 'claude' && dreamTheme.claude_theme) {
                return (
                <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-orange-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                  
                  {renderAnimatedBackground(dreamTheme.claude_theme?.colors, '#FFA50010')}

                  {renderThemeHeader('claude', '/claude.png', 'compare')}

                  {/* Claude Theme Content - Enhanced */}
                  <div className="space-y-6 relative z-10">
                    <div className="bg-gradient-to-br from-rose-50/90 via-pink-50/90 to-champagne-50/90 rounded-2xl p-6 border-2 border-rose-200/40 shadow-lg hover:shadow-xl transition-all duration-300 group">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-3 animate-bounce">🎨</span>
                        <h5 className="text-xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">Theme & Style</h5>
                      </div>
                      <h6 className="text-2xl font-bold text-gray-800 mb-3">{dreamTheme.claude_theme?.theme_name || 'Theme Name'}</h6>
                      <p className="text-gray-700 leading-relaxed font-light">{dreamTheme.claude_theme?.description || 'Theme description'}</p>
                    </div>

                    {/* Inspiration Text */}
                    {formData.text && (
                      <div className="group">
                        <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gold-600 group-hover:to-yellow-600 group-hover:bg-clip-text transition-all duration-300">
                          <span className="mr-3 text-3xl animate-bounce">💡</span> Original Inspiration
                        </h3>
                        <div className="bg-gradient-to-br from-gold-50 via-yellow-25 to-champagne-50 p-6 rounded-3xl border-2 border-gold-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                          <p className="text-gray-700 leading-relaxed text-lg font-light italic">
                            "{formData.text}"
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Color Palette */}
                    {wrappedRenderColorPalette(dreamTheme.claude_theme?.colors, 'claude', 'individual')}

                    {/* Claude Style Keywords */}
                    {dreamTheme.claude_theme?.style_keywords && renderStyleKeywords(dreamTheme.claude_theme.style_keywords, 'individual')}

                    {/* Claude Key Elements */}
                    {dreamTheme.claude_theme?.elements && renderElements(dreamTheme.claude_theme.elements, 'individual')}
                  </div>

                  {/* Save Claude Theme Button */}
                  {wrappedRenderSaveButton(dreamTheme.claude_theme, 'Claude')}

                  {/* Ritual Suggestions */}
                  {renderRitualSection(dreamTheme.claude_theme?.suggested_rituals, dreamTheme.claude_theme?.theme_name, setRitualModal, null, dreamTheme.claude_theme?.id)}

                  {/* Theme Feedback Section */}
                  <ThemeFeedback
                    key="claude"
                    featureType="dream_generator"
                    contentId={null}
                    themeName={dreamTheme.claude_theme?.theme_name}
                    aiModel={dreamTheme.claude_theme?.generated_by}
                    qualityScore={dreamTheme.claude_theme?.quality_score}
                    initialRating={compareFeedback.claude.rating}
                    initialThumbs={compareFeedback.claude.thumbs}
                    onFeedbackChange={(r, t) => setCompareFeedback(prev => ({ ...prev, claude: { rating: r, thumbs: t } }))}
                    onSuccess={handleFeedbackSuccess}
                  />
                </div>
                );
              
              // OPENAI TAB - OpenAI's GPT model results
              } else if (activeTab === 'openai' && dreamTheme.openai_theme) {
                return (
                  <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-green-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                  
                  {renderAnimatedBackground(dreamTheme.openai_theme?.colors, '#4F46E510')}

                  {renderThemeHeader('openai', '/openai.png', 'compare')}

                  {/* OpenAI Theme Content - Enhanced */}
                  <div className="space-y-6 relative z-10">
                    <div className="bg-gradient-to-br from-blue-50/90 via-indigo-50/90 to-purple-50/90 rounded-2xl p-6 border-2 border-blue-200/40 shadow-lg hover:shadow-xl transition-all duration-300 group">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-3 animate-bounce">🎨</span>
                        <h5 className="text-xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text transition-all duration-300">Theme & Style</h5>
                      </div>
                      <h6 className="text-2xl font-bold text-gray-800 mb-3">{dreamTheme.openai_theme?.theme_name || 'Theme Name'}</h6>
                      <p className="text-gray-700 leading-relaxed font-light">{dreamTheme.openai_theme?.description || 'Theme description'}</p>
                    </div>

                    {/* Inspiration Text */}
                    {formData.text && (
                      <div className="group">
                        <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gold-600 group-hover:to-yellow-600 group-hover:bg-clip-text transition-all duration-300">
                          <span className="mr-3 text-3xl animate-bounce">💡</span> Original Inspiration
                        </h3>
                        <div className="bg-gradient-to-br from-gold-50 via-yellow-25 to-champagne-50 p-6 rounded-3xl border-2 border-gold-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                          <p className="text-gray-700 leading-relaxed text-lg font-light italic">
                            "{formData.text}"
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Color Palette */}
                    {wrappedRenderColorPalette(dreamTheme.openai_theme?.colors, 'openai', 'individual')}

                    {/* OpenAI Style Keywords */}
                    {dreamTheme.openai_theme?.style_keywords && renderStyleKeywords(dreamTheme.openai_theme.style_keywords, 'individual')}

                    {/* OpenAI Key Elements */}
                    {dreamTheme.openai_theme?.elements && renderElements(dreamTheme.openai_theme.elements, 'individual')}
                  </div>

                  {/* Save OpenAI Theme Button */}
                  {/* Save OpenAI Theme Button */}
                  {wrappedRenderSaveButton(dreamTheme.openai_theme, 'OpenAI')}

                  {/* Ritual Suggestions */}
                  {renderRitualSection(dreamTheme.openai_theme?.suggested_rituals, dreamTheme.openai_theme?.theme_name, setRitualModal, null, dreamTheme.openai_theme?.id)}

                  {/* Theme Feedback Section */}
                  <ThemeFeedback
                    key="openai"
                    featureType="dream_generator"
                    contentId={null}
                    themeName={dreamTheme.openai_theme?.theme_name}
                    aiModel={dreamTheme.openai_theme?.generated_by}
                    qualityScore={dreamTheme.openai_theme?.quality_score}
                    initialRating={compareFeedback.openai.rating}
                    initialThumbs={compareFeedback.openai.thumbs}
                    onFeedbackChange={(r, t) => setCompareFeedback(prev => ({ ...prev, openai: { rating: r, thumbs: t } }))}
                    onSuccess={handleFeedbackSuccess}
                  />
                </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>
    </div>
    
    {/* Color Information Modal - VowAI Styled */}
    
    {/* AI Color Magic Modal - Shared component from utils/modalRenderers */}
    {renderAIColorMagicModal(
      colorModal,
      setColorModal,
      handleGetDisplayApplications,
      handleTriggerAIMagic,
      hexToRgb,
      rgbToHsl,
      categories,
      AIMagicButton,
      AIColorMagicLoader
    )}
    
    <NotificationModal
      isOpen={notification.show}
      title={notification.title}
      message={notification.message}
      type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
      onClose={() => setNotification({ show: false, message: '', type: '', title: '' })}
    />

    {/* CSS Animations */}
    <style jsx>{`
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fadeInUp {
        animation: fadeInUp 0.6s ease-out;
      }
    `}</style>

    {/* Ritual Details Modal */}
    {renderRitualModal(ritualModal, setRitualModal, null, dreamTheme?.id, savedRitualIds, handleSaveRitual, dreamTheme?.theme_name || dreamTheme?.gemini_theme?.theme_name || dreamTheme?.claude_theme?.theme_name || dreamTheme?.openai_theme?.theme_name)}
    </>
  );
};

export default DreamWeddingGenerator;
